import React, { useContext, useEffect, useRef, useState } from "react";
import {
    Modal,
    Box,
    Typography,
    Button,
    Radio,
    RadioGroup,
    FormControlLabel,
    IconButton,
    Grid,
    Card,
    CardContent,
    TextField,
    FormControl,
    Autocomplete,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import "../../assets/styles/AddModal.scss";
import { CommonContext, WarehouseMasterContext } from "../../context";
import commonApi from "../../utils/api";
import AlertMessage from "../common/AlertMessage";
import { states } from "../../utils/config";
import { scrollToTop } from "../common/scrollToTop";

const initialFormState = {
    name: "",
    status: "1",
    contact_person: "",
    contact_mobile: "",
    total_emp: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    area_per_sqft: ""
};

const AddModal = () => {
    const { modalAlert, setModalAlert, setAlertMessage, setSeverity, user } =
        useContext(CommonContext);
    const {
        addModal,
        handleCloseModal,
        onLoadApi,
        viewData,
        isEdit,
        setIsLoading,
    } = useContext(WarehouseMasterContext);

    const [formDataTemp, setFormDataTemp] = useState(initialFormState);
    const [formErrors, setFormErrors] = useState({});
    const modalContentRef = useRef(null);

    useEffect(() => {
        if (viewData && isEdit) {
            setFormDataTemp({
                ...viewData,
                status: viewData.status?.toString() || "1"
            });
        } else {
            setFormDataTemp(initialFormState);
        }
        setFormErrors({});
    }, [viewData, isEdit]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormDataTemp((prev) => ({ ...prev, [name]: value }));
    };

    const validateSingle = (name, value) => {
        switch (name) {
            case "name":
                return value ? "" : "Name is required";
            case "contact_person":
                return value ? "" : "Contact Person is required";
            case "contact_mobile":
                if (!value) return "Mobile number is required";
                return /^[0-9]{10}$/.test(value)
                    ? ""
                    : "Invalid mobile number (10 digits required)";
            case "address":
                return value ? "" : "Address is required";
            case "city":
                return value ? "" : "City is required";
            case "state":
                return value ? "" : "State is required";
            case "pincode":
                return value ? "" : "Pincode is required";
            case "total_emp":
                return value ? "" : "Total Employees is required";
            case "area_per_sqft":
                return value ? "" : "Area per sqft is required";
            default:
                return "";
        }
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        const err = validateSingle(name, formDataTemp[name]);
        setFormErrors((prev) => ({ ...prev, [name]: err }));
    };

    const validateForm = () => {
        const errors = {};
        Object.keys(initialFormState).forEach((key) => {
            const err = validateSingle(key, formDataTemp[key]);
            if (err) errors[key] = err;
        });
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsLoading(true);

        const payload = {
            ...formDataTemp,
            status: parseInt(formDataTemp.status),
            total_emp: parseInt(formDataTemp.total_emp),
            area_per_sqft: parseFloat(formDataTemp.area_per_sqft),
            state: parseInt(formDataTemp.state)
        };

        const url = isEdit ? "/warehouse-master/edit" : "/warehouse-master/add";

        try {
            const res = await commonApi.post(url, payload);
            setModalAlert(true);
            scrollToTop(modalContentRef);

            if (res?.data?.s === 1) {
                setAlertMessage(res?.data?.msg || "Data saved successfully.");
                setSeverity("success");
                setFormDataTemp(initialFormState);
                setTimeout(() => {
                    handleCloseModal();
                    onLoadApi();
                }, 1500);
            } else {
                setAlertMessage(res?.data?.msg || "Something went wrong.");
                setSeverity("error");
            }
        } catch (err) {
            scrollToTop(modalContentRef);
            setModalAlert(true);
            setAlertMessage(err?.message || "Error submitting form.");
            setSeverity("error");
            console.error("Error submitting form:", err);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal open={addModal}>
            <Box className="add-modal">
                <Box className="modal-header">
                    <Typography variant="h6">
                        {isEdit ? "Edit" : "Add"} Warehouse Master
                    </Typography>
                    <IconButton onClick={handleCloseModal} size="small">
                        <Close />
                    </IconButton>
                </Box>

                <Box className="content-body" ref={modalContentRef}>
                    {modalAlert && <AlertMessage />}

                    <form className="custom-form" onSubmit={handleSubmit} noValidate>
                        <Card className="form-section">
                            <Typography variant="h6" className="header-title">Warehouse Information</Typography>
                            <CardContent>
                                <Grid container spacing={2}>
                                    <Grid item size={{ xs: 6, md: 4 }}>
                                        <TextField
                                            label="Warehouse Name"
                                            name="name"
                                            value={formDataTemp.name}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            fullWidth
                                            size="small"
                                            error={!!formErrors.name}
                                            helperText={formErrors.name}
                                        />
                                    </Grid>
                                    <Grid item size={{ xs: 6, md: 4 }}>
                                        <TextField
                                            label="Contact Person"
                                            name="contact_person"
                                            value={formDataTemp.contact_person}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            fullWidth
                                            size="small"
                                            error={!!formErrors.contact_person}
                                            helperText={formErrors.contact_person}
                                        />
                                    </Grid>
                                    <Grid item size={{ xs: 6, md: 4 }}>
                                        <TextField
                                            label="Contact Mobile"
                                            name="contact_mobile"
                                            value={formDataTemp.contact_mobile}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            fullWidth
                                            size="small"
                                            error={!!formErrors.contact_mobile}
                                            helperText={formErrors.contact_mobile}
                                        />
                                    </Grid>
                                    <Grid item size={{ xs: 6, md: 4 }}>
                                        <TextField
                                            label="Total Employees"
                                            name="total_emp"
                                            type="number"
                                            value={formDataTemp.total_emp}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            fullWidth
                                            size="small"
                                            error={!!formErrors.total_emp}
                                            helperText={formErrors.total_emp}
                                        />
                                    </Grid>
                                    <Grid item size={{ xs: 6, md: 4 }}>
                                        <TextField
                                            label="Area (sqft)"
                                            name="area_per_sqft"
                                            type="number"
                                            value={formDataTemp.area_per_sqft}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            fullWidth
                                            size="small"
                                            error={!!formErrors.area_per_sqft}
                                            helperText={formErrors.area_per_sqft}
                                        />
                                    </Grid>
                                    <Grid item size={{ xs: 6, md: 4 }}>
                                        <TextField
                                            label="City"
                                            name="city"
                                            value={formDataTemp.city}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            fullWidth
                                            size="small"
                                            error={!!formErrors.city}
                                            helperText={formErrors.city}
                                        />
                                    </Grid>
                                    <Grid item size={{ xs: 6, md: 4 }}>
                                        <Autocomplete
                                            options={Object.entries(states).map(([key, value]) => ({
                                                id: key,
                                                label: value,
                                            }))}
                                            getOptionLabel={(option) => option.label || ""}
                                            value={
                                                Object.entries(states)
                                                    .map(([key, value]) => ({ id: key, label: value }))
                                                    .find((item) => item.id.toString() === formDataTemp?.state?.toString()) || null
                                            }
                                            onChange={(event, newValue) => {
                                                handleChange({
                                                    target: {
                                                        name: "state",
                                                        value: newValue ? newValue.id : "",
                                                    },
                                                });
                                            }}
                                            onBlur={() => {
                                                handleBlur({
                                                    target: {
                                                        name: "state",
                                                        value: formDataTemp?.state,
                                                    },
                                                });
                                            }}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                            sx={{
                                                "& .MuiInputBase-root": { paddingTop: "0", paddingBottom: "0" },
                                                margin: "0.5rem 0",
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="State"
                                                    variant="outlined"
                                                    error={!!formErrors?.state}
                                                    helperText={formErrors?.state}
                                                    fullWidth
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item size={{ xs: 6, md: 4 }}>
                                        <TextField
                                            label="Pincode"
                                            name="pincode"
                                            value={formDataTemp.pincode}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            fullWidth
                                            size="small"
                                            error={!!formErrors.pincode}
                                            helperText={formErrors.pincode}
                                        />
                                    </Grid>
                                    <Grid item size={{ xs: 12, md: 12 }}>
                                        <TextField
                                            multiline
                                            minRows={2}
                                            label="Address"
                                            name="address"
                                            value={formDataTemp.address}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            fullWidth
                                            size="small"
                                            error={!!formErrors.address}
                                            helperText={formErrors.address}
                                        />
                                    </Grid>
                                    <Grid item size={{ xs: 6, md: 4 }}>
                                        <FormControl component="fieldset">
                                            <RadioGroup
                                                name="status"
                                                value={formDataTemp.status}
                                                onChange={handleChange}
                                                row
                                            >
                                                <FormControlLabel
                                                    value="1"
                                                    control={<Radio />}
                                                    label="Active"
                                                />
                                                <FormControlLabel
                                                    value="0"
                                                    control={<Radio />}
                                                    label="Inactive"
                                                />
                                            </RadioGroup>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </form>
                </Box>

                <Box className="form-actions">
                    <Button
                        variant="outlined"
                        onClick={handleCloseModal}
                        color="error"
                        type="button"
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        type="submit"
                        onClick={handleSubmit}
                    >
                        Submit
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default AddModal;
